import { computeProgressEstimation } from './computeProgressEstimation'

import type { ProgressCellValue, ProgressTableRow } from './progressTableTypes'
import type {
  ProjectTrackerData,
  ProjectTrackerDataFloor,
  ProjectTrackerDataZone,
} from '../../../api/trackers'

export const ESTIMATED_PROGRESS_TOOLTIP =
  'Some floors are missing data. This is an estimated value based on available data.'

const RESERVED_FLOOR_KEYS = ['floor_code', 'floor', 'Overview', 'zones']

/** Returns keys to use for job-type columns (excludes floor_code, floor, Overview, zones). */
export function getJobTypeKeys(floor: ProjectTrackerDataFloor): string[] {
  return Object.keys(floor).filter(
    (key: string) => !RESERVED_FLOOR_KEYS.includes(key),
  )
}

/**
 * Job-type column keys for a tracker, unioning all physical floors (skips Overview).
 * The first floor in API data is often Overview and may omit job keys; using only
 * floors[0] misses columns that exist on lower floors (e.g. Electrical).
 */
export function unionJobTypeKeysForTracker(
  tracker: ProjectTrackerData,
): string[] {
  const seen = new Set<string>()
  const ordered: string[] = []
  for (const floor of tracker.floors ?? []) {
    if (floor?.floor === 'Overview') continue
    for (const key of getJobTypeKeys(floor)) {
      if (!seen.has(key)) {
        seen.add(key)
        ordered.push(key)
      }
    }
  }
  return ordered
}

const ZONE_ROW_META_KEYS = ['zone_id', 'zone_name', 'Overview'] as const

function isUsableProgressCell(v: unknown): v is ProgressCellValue {
  return (
    v != null &&
    typeof v === 'object' &&
    typeof (v as ProgressCellValue).total_units === 'number' &&
    (v as ProgressCellValue).total_units! > 0
  )
}

/**
 * Zones often have no `Overview` from the API (unlike floors). Sum all job-type
 * cells on the zone so the Overview column matches the cross-job rollup users expect.
 */
function synthesizeZoneOverviewFromJobTypes(
  trackerZone: ProjectTrackerDataZone,
): ProgressCellValue | null {
  let acc: ProgressCellValue | null = null
  for (const key of Object.keys(trackerZone)) {
    if ((ZONE_ROW_META_KEYS as readonly string[]).includes(key)) continue
    const cell = trackerZone[key] as ProgressCellValue | undefined
    if (!isUsableProgressCell(cell)) continue
    acc = acc ? aggregateOverviewCell(acc, { ...cell }) : { ...cell }
  }
  return acc
}

/** Prefer API `Overview` on the zone; otherwise roll up job-type columns. */
function zoneOverviewCellForTracker(
  trackerZone: ProjectTrackerDataZone | undefined,
  trackerName: string | undefined,
  floorCode: string | null,
): ProgressCellValue | null {
  if (!trackerZone) return null
  const apiOverview = trackerZone.Overview as ProgressCellValue | undefined
  if (isUsableProgressCell(apiOverview)) {
    return {
      ...apiOverview,
      trackerName,
      floorCode,
    }
  }
  const synthesized = synthesizeZoneOverviewFromJobTypes(trackerZone)
  if (!synthesized) return null
  return {
    ...synthesized,
    trackerName,
    floorCode,
  }
}

/** Merges two Overview cell objects (total_units, completed_units, dates). */
export function aggregateOverviewCell(
  prev: ProgressCellValue,
  next: ProgressCellValue,
): ProgressCellValue {
  const merged: ProgressCellValue = {
    ...prev,
    total_units: prev.total_units + next.total_units,
    completed_units: prev.completed_units + next.completed_units,
  }
  if (next.initial_observation_date) {
    if (
      !prev.initial_observation_date ||
      new Date(next.initial_observation_date) <=
        new Date(prev.initial_observation_date)
    ) {
      merged.initial_observation_date = next.initial_observation_date
    }
  }
  if (next.latest_observation_date) {
    if (
      !prev.latest_observation_date ||
      new Date(next.latest_observation_date) >=
        new Date(prev.latest_observation_date)
    ) {
      merged.latest_observation_date = next.latest_observation_date
    }
  }
  return merged
}

/** Returns new object with trackerName/floorCode on each job-type cell; no mutation. */
export function patchTrackerFloorWithMeta(
  trackerFloor: ProjectTrackerDataFloor,
  trackerName: string | undefined,
  floorCode: string | number | null,
): Record<string, ProgressCellValue> {
  const result: Record<string, ProgressCellValue> = {}
  const jobTypeKeys = getJobTypeKeys(trackerFloor)
  for (const jobType of jobTypeKeys) {
    const cell = trackerFloor[jobType] as ProgressCellValue | undefined
    if (cell && typeof cell === 'object') {
      result[jobType] = {
        ...cell,
        trackerName,
        floorCode: floorCode != null ? String(floorCode) : null,
      }
    }
  }
  return result
}

/** Overview row only: returns floorEntry with estimation fields set when criteria are met. */
function applyOverviewEstimation(
  floor: ProjectTrackerDataFloor,
  floorEntry: ProgressTableRow,
  trackerData: ProjectTrackerData[],
): ProgressTableRow {
  const isOverviewRow = floor?.floor === 'Overview'
  const physicalFloors = (trackerData[0]?.floors ?? []).filter(
    (f: ProjectTrackerDataFloor) => f?.floor !== 'Overview',
  )
  const physicalFloorCount = physicalFloors.length

  if (!isOverviewRow || physicalFloorCount < 2) {
    return floorEntry
  }

  const updatedEntry = { ...floorEntry }

  for (const tracker of trackerData) {
    const jobTypeKeys = getJobTypeKeys(tracker.floors[0] ?? {})

    for (const jobType of jobTypeKeys) {
      const totalsWithData: number[] = []
      let completedSum = 0
      let floorsWithoutData = 0

      let hasExplicitZeroProgressFloor = false
      for (let j = 0; j < tracker.floors.length; j++) {
        if (tracker.floors[j]?.floor === 'Overview') continue
        const cellData = tracker.floors[j]?.[jobType] as
          | { total_units?: number; completed_units?: number }
          | undefined
        const total = cellData?.total_units ?? 0
        const completed = cellData?.completed_units ?? 0
        if (total > 0) {
          totalsWithData.push(total)
          completedSum += completed
          if (completed === 0) {
            hasExplicitZeroProgressFloor = true
          }
        } else {
          floorsWithoutData += 1
        }
      }

      const result = computeProgressEstimation(
        totalsWithData,
        completedSum,
        floorsWithoutData,
      )
      const existingCell = floorEntry[jobType] as ProgressCellValue | undefined
      if (!result.criteriaMet || !existingCell) continue
      if (floorsWithoutData >= 1 && !hasExplicitZeroProgressFloor) {
        continue
      }

      updatedEntry[jobType] = {
        ...existingCell,
        total_units: result.estimatedTotal,
        completed_units: result.estimatedCompleted,
        isEstimatedOverview: true,
        estimatedTooltipText: ESTIMATED_PROGRESS_TOOLTIP,
      }
    }
  }

  // Overview column: apply same threshold criteria using per-floor Overview data (aggregated across trackers)
  const overviewFloorIndex = (trackerData[0]?.floors ?? []).findIndex(
    (f: ProjectTrackerDataFloor) => f?.floor === 'Overview',
  )
  if (overviewFloorIndex !== -1 && updatedEntry.Overview) {
    const overviewTotalsWithData: number[] = []
    let overviewCompletedSum = 0
    let overviewWithoutData = 0
    const floors = trackerData[0]?.floors ?? []

    let overviewHasExplicitZeroProgressFloor = false
    for (let j = 0; j < floors.length; j++) {
      if (j === overviewFloorIndex) continue
      let floorTotal = 0
      let floorCompleted = 0
      for (const tracker of trackerData) {
        const overviewCell = tracker.floors[j]?.Overview as
          | { total_units?: number; completed_units?: number }
          | undefined
        floorTotal += overviewCell?.total_units ?? 0
        floorCompleted += overviewCell?.completed_units ?? 0
      }
      if (floorTotal > 0) {
        overviewTotalsWithData.push(floorTotal)
        overviewCompletedSum += floorCompleted
        if (floorCompleted === 0) {
          overviewHasExplicitZeroProgressFloor = true
        }
      } else {
        overviewWithoutData += 1
      }
    }

    const overviewResult = computeProgressEstimation(
      overviewTotalsWithData,
      overviewCompletedSum,
      overviewWithoutData,
    )
    if (
      overviewResult.criteriaMet &&
      (overviewWithoutData < 1 || overviewHasExplicitZeroProgressFloor)
    ) {
      updatedEntry.Overview = {
        ...updatedEntry.Overview,
        total_units: overviewResult.estimatedTotal,
        completed_units: overviewResult.estimatedCompleted,
        isEstimatedOverview: true,
        estimatedTooltipText: ESTIMATED_PROGRESS_TOOLTIP,
      }
    }
  }

  return updatedEntry
}

/** Builds one zone row object from zone + trackerData. */
function buildZoneEntry(
  zone: ProjectTrackerDataZone,
  floorIndex: number,
  trackerData: ProjectTrackerData[],
): ProgressTableRow {
  const zoneEntry: ProgressTableRow = {
    floor: zone.zone_name,
    floor_code: zone.zone_id,
    isZone: true,
    parentFloorCode: (
      trackerData[0]?.floors?.[floorIndex] as
        | ProjectTrackerDataFloor
        | undefined
    )?.floor_code as number | undefined,
  }

  for (const tracker of trackerData) {
    const trackerFloor = tracker.floors[floorIndex]
    if (!trackerFloor?.zones) continue
    const trackerZone = trackerFloor.zones.find(
      (z: ProjectTrackerDataZone) => z && z.zone_id === zone.zone_id,
    )
    if (trackerZone) {
      const trackerName = tracker?.type?.name
      const jobTypeKeys = Object.keys(trackerZone).filter(
        (key: string) => !['zone_id', 'zone_name', 'Overview'].includes(key),
      )
      for (const jobType of jobTypeKeys) {
        const cell = trackerZone[jobType] as ProgressCellValue | undefined
        if (cell && typeof cell === 'object') {
          zoneEntry[jobType] = {
            ...cell,
            trackerName,
            floorCode: null,
          }
        }
      }
      const overviewPart = zoneOverviewCellForTracker(
        trackerZone,
        trackerName,
        null,
      )
      if (overviewPart) {
        zoneEntry.Overview = zoneEntry.Overview
          ? aggregateOverviewCell(zoneEntry.Overview, overviewPart)
          : { ...overviewPart }
      }
    }
  }

  return zoneEntry
}

function buildPhysicalFloorRowAtIndex(
  floors: ProjectTrackerDataFloor[],
  trackerData: ProjectTrackerData[],
  i: number,
): ProgressTableRow {
  const floor = floors[i]
  const floorCode =
    floor?.floor === 'Overview'
      ? null
      : (floor?.floor_code as string | number | null)
  let floorEntry: ProgressTableRow = {
    floor: floor?.floor ?? '',
  }
  if (floor?.floor_code != null) {
    floorEntry.floor_code = floor.floor_code as number
  }

  for (const tracker of trackerData) {
    const trackerFloor = { ...tracker.floors[i] } as ProjectTrackerDataFloor
    const grandOverview = { ...(trackerFloor.Overview as ProgressCellValue) }
    delete (trackerFloor as Record<string, unknown>).Overview

    const trackerName = tracker?.type?.name
    const jobTypePatch = patchTrackerFloorWithMeta(
      trackerFloor,
      trackerName,
      floorCode,
    )

    const overviewWithMeta: ProgressCellValue = {
      ...grandOverview,
      trackerName,
      floorCode: floorCode != null ? String(floorCode) : null,
    }

    if (floorEntry.Overview) {
      floorEntry.Overview = aggregateOverviewCell(
        floorEntry.Overview,
        overviewWithMeta,
      )
    } else {
      floorEntry.Overview = { ...overviewWithMeta }
    }

    floorEntry = { ...floorEntry, ...jobTypePatch }
  }

  floorEntry = applyOverviewEstimation(floor, floorEntry, trackerData)

  if (floor?.zones && Array.isArray(floor.zones) && floor.zones.length > 0) {
    floorEntry.hasZones = true
    floorEntry.zonesCount = floor.zones.length
  }

  return floorEntry
}

function buildFloorEntries(
  floors: ProjectTrackerDataFloor[],
  trackerData: ProjectTrackerData[],
): ProgressTableRow[] {
  const rows: ProgressTableRow[] = []

  for (let i = 0; i < floors.length; i++) {
    const floor = floors[i]
    const floorEntry = buildPhysicalFloorRowAtIndex(floors, trackerData, i)
    rows.push(floorEntry)

    if (floor?.zones && Array.isArray(floor.zones) && floor.zones.length > 0) {
      const sortedZones = [...floor.zones].sort(
        (a: ProjectTrackerDataZone, b: ProjectTrackerDataZone) =>
          a.zone_id - b.zone_id,
      )
      for (const zone of sortedZones) {
        if (
          zone == null ||
          typeof zone.zone_id === 'undefined' ||
          !zone.zone_name
        ) {
          continue
        }
        rows.push(buildZoneEntry(zone, i, trackerData))
      }
    }
  }

  return rows
}

function buildZoneFloorBreakdownRow(
  zone: ProjectTrackerDataZone,
  floorIndex: number,
  parentZoneName: string,
  trackerData: ProjectTrackerData[],
): ProgressTableRow {
  const baseFloor = trackerData[0].floors[floorIndex] as
    | ProjectTrackerDataFloor
    | undefined
  const floorCodeVal = baseFloor?.floor_code
  const floorCodeStr = floorCodeVal != null ? String(floorCodeVal) : null

  const row: ProgressTableRow = {
    floor: baseFloor?.floor ?? '',
    floor_code: floorCodeVal as number | undefined,
    isZoneFloorBreakdown: true,
    parentZoneName,
  }

  for (const tracker of trackerData) {
    const trackerFloor = tracker.floors[floorIndex]
    if (!trackerFloor?.zones) continue
    const trackerZone = trackerFloor.zones.find(
      (z: ProjectTrackerDataZone) => z && z.zone_id === zone.zone_id,
    )
    if (trackerZone) {
      const trackerName = tracker?.type?.name
      const jobTypeKeys = Object.keys(trackerZone).filter(
        (key: string) => !['zone_id', 'zone_name', 'Overview'].includes(key),
      )
      for (const jobType of jobTypeKeys) {
        const cell = trackerZone[jobType] as ProgressCellValue | undefined
        if (cell && typeof cell === 'object') {
          row[jobType] = {
            ...cell,
            trackerName,
            floorCode: floorCodeStr,
          }
        }
      }
      const overviewPart = zoneOverviewCellForTracker(
        trackerZone,
        trackerName,
        floorCodeStr,
      )
      if (overviewPart) {
        row.Overview = row.Overview
          ? aggregateOverviewCell(row.Overview, overviewPart)
          : { ...overviewPart }
      }
    }
  }

  return row
}

function buildZoneAggregateRow(
  zoneName: string,
  instances: { floorIndex: number; zone: ProjectTrackerDataZone }[],
  trackerData: ProjectTrackerData[],
): ProgressTableRow {
  const row: ProgressTableRow = {
    floor: zoneName,
    isZoneAggregate: true,
    zoneCollapseKey: zoneName,
    hasZones: true,
    zonesCount: instances.length,
  }

  for (const tracker of trackerData) {
    const trackerName = tracker?.type?.name
    const perTrackerMerged: Record<string, ProgressCellValue> = {}
    let perTrackerOverview: ProgressCellValue | null = null

    for (const { floorIndex, zone } of instances) {
      const trackerFloor = tracker.floors[floorIndex]
      const trackerZone = trackerFloor?.zones?.find(
        (z: ProjectTrackerDataZone) => z && z.zone_id === zone.zone_id,
      )
      if (!trackerZone) continue
      const jobTypeKeys = Object.keys(trackerZone).filter(
        (key: string) => !['zone_id', 'zone_name', 'Overview'].includes(key),
      )
      for (const jobType of jobTypeKeys) {
        const cell = trackerZone[jobType] as ProgressCellValue | undefined
        if (!cell || typeof cell !== 'object') continue
        const withMeta: ProgressCellValue = {
          ...cell,
          trackerName,
          floorCode: null,
        }
        const existing = perTrackerMerged[jobType]
        if (existing && typeof existing === 'object') {
          perTrackerMerged[jobType] = aggregateOverviewCell(existing, withMeta)
        } else {
          perTrackerMerged[jobType] = { ...withMeta }
        }
      }

      const overviewPart = zoneOverviewCellForTracker(
        trackerZone,
        trackerName,
        null,
      )
      if (overviewPart) {
        perTrackerOverview = perTrackerOverview
          ? aggregateOverviewCell(perTrackerOverview, overviewPart)
          : { ...overviewPart }
      }
    }

    for (const jobType of Object.keys(perTrackerMerged)) {
      row[jobType] = perTrackerMerged[jobType]
    }

    if (perTrackerOverview) {
      row.Overview = row.Overview
        ? aggregateOverviewCell(row.Overview, perTrackerOverview)
        : { ...perTrackerOverview }
    }
  }

  return row
}

export function projectHasZoneData(trackerData: ProjectTrackerData[]): boolean {
  const floors = trackerData[0]?.floors ?? []
  return floors.some(
    (f: ProjectTrackerDataFloor) =>
      f?.floor !== 'Overview' && !!f.zones && f.zones.length > 0,
  )
}

export function buildZoneFocusedProgressTableData(
  trackerData: ProjectTrackerData[],
): ProgressTableRow[] {
  if (!trackerData?.[0]?.floors?.length) {
    return []
  }

  const floors = trackerData[0].floors
  const overviewIndex = floors.findIndex(
    (f: ProjectTrackerDataFloor) => f?.floor === 'Overview',
  )
  if (overviewIndex < 0) {
    return []
  }

  const rows: ProgressTableRow[] = [
    buildPhysicalFloorRowAtIndex(floors, trackerData, overviewIndex),
  ]

  const physical = floors
    .map((floor: ProjectTrackerDataFloor, index: number) => ({
      floor,
      index,
    }))
    .filter(
      ({ floor }: { floor: ProjectTrackerDataFloor }) =>
        floor?.floor !== 'Overview',
    )

  const byZoneName = new Map<
    string,
    { floorIndex: number; zone: ProjectTrackerDataZone }[]
  >()

  for (const { floor, index } of physical) {
    for (const z of floor.zones ?? []) {
      if (z == null || !z.zone_name) continue
      const list = byZoneName.get(z.zone_name) ?? []
      list.push({ floorIndex: index, zone: z })
      byZoneName.set(z.zone_name, list)
    }
  }

  const sortedNames = Array.from(byZoneName.keys()).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' }),
  )

  for (const zoneName of sortedNames) {
    const instances = byZoneName.get(zoneName)!
    instances.sort((a, b) => a.floorIndex - b.floorIndex)
    rows.push(buildZoneAggregateRow(zoneName, instances, trackerData))
    for (const { floorIndex, zone } of instances) {
      rows.push(
        buildZoneFloorBreakdownRow(zone, floorIndex, zoneName, trackerData),
      )
    }
  }

  for (const { floor, index } of physical) {
    if (!floor.zones?.length) {
      rows.push(buildPhysicalFloorRowAtIndex(floors, trackerData, index))
    }
  }

  return rows
}

export function getProgressDashboardPageSize(
  trackerData: ProjectTrackerData[],
  zoneFocused: boolean,
): number {
  if (!trackerData?.length || !trackerData[0]?.floors) {
    return 10
  }
  if (zoneFocused && projectHasZoneData(trackerData)) {
    return Math.max(buildZoneFocusedProgressTableData(trackerData).length, 10)
  }
  const floorsCount = trackerData[0].floors.length ?? 10
  const zonesCount = trackerData[0].floors.reduce(
    (acc: number, floor: ProjectTrackerDataFloor) =>
      acc + (floor.zones?.length ?? 0),
    0,
  )
  return Math.max(floorsCount + zonesCount + 1, 10)
}

export function buildProgressTableData(
  trackerData: ProjectTrackerData[],
): ProgressTableRow[] {
  if (!trackerData || trackerData.length === 0 || !trackerData[0].floors) {
    return []
  }

  const rows = buildFloorEntries(trackerData[0].floors, trackerData)

  return rows
}
